import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useErrorBoundary } from "react-error-boundary";
import { useMediaQuery } from 'react-responsive';

import { useAuth } from '../contexts/AuthContext';
import PoolHeader from '../components/pools/PoolHeader';
import { MainContainer } from '../components/common/Containers';
import Banner from '../components/common/Banners';
import UserPoolCard from '../components/users/UserPoolCard';
import { PoolMeeting, PoolGroupMe, SectionLocked, AboutPool } from '../components/pools/PoolSection';
import { PoolTimeGraphic } from '../components/pools/PoolGraphic';
import PoolFAQ from '../components/pools/PoolFAQ';
import Loading from '../components/common/Loading';
import { myAxiosInstance } from '../api/axiosConfig';
import { ReactComponent as NothingYetIcon } from "../assets/icons/nothingYet.svg"; // Import SVG as ReactComponent


export default function DemoPool() {

    // Auth
    const { userFirstName, userSlug } = useAuth();

    // Get the theme
    const theme = useTheme();

    // Responsive
    const isMobile = useMediaQuery({ maxWidth: 768 });

    // States
    const [activeTab, setActiveTab] = useState("about");
    const [userStatus, setUserStatus] = useState("member");

    const tabOptions = ["about", "members", "faqs", "vote"];
    
    const poolData =
    {
        "public_details": {
            "slug": "yop0m",
            "name": "VEGA",
            "pool_package": "Alpha",
            "description": "",
            "threshold": 1000000,
            "contribution_percent": 2,
            "time_horizon": 10,
            "minimum_income": 50000,
            "created_at": "2024-04-22T02:25:27.135097Z",
            "invitation_deadline": "2024-04-26T03:59:00Z",
            "sign_deadline": new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 1 hour from now
            "member_count": 6
        },
        "other_users": [
            {
                "slug": "sam-antonyan-lj42",
                "email": "sam.antonyan@yale.edu",
                "first_name": "Andrew",
                "last_name": "Johnson",
                "university": "Yale University",
                "major_or_grad_program": [
                    "Mathematics",
                    "Physics"
                ],
                "graduation_date": 2026,
                "company": "Google",
                "tag_line": "SWE @ Google | AI",
                "profile_picture": "https://images.prospinity.com/profile_pictures/public_images/user1.png",
                "profile_picture_url": "https://images.prospinity.com/profile_pictures/public_images/user1.png",
                "portfolio_count": 23,
                "pooled": true,
                "is_verified": true
            },
            {
                "slug": "jonas-katona-OBXg",
                "email": "jonas.katona@yale.edu",
                "first_name": "Anna",
                "last_name": "Smith",
                "university": "Stanford University",
                "major_or_grad_program": [
                    "Applied Mathematics - PhD"
                ],
                "graduation_date": 2025,
                "tag_line": "Rocket Engineer | NASA",
                "company": "NASA",
                "profile_picture": "https://images.prospinity.com/profile_pictures/public_images/user2.png",
                "profile_picture_url": "https://images.prospinity.com/profile_pictures/public_images/user2.png",
                "portfolio_count": 19,
                "pooled": true,
                "is_verified": true
            },
            {
                "slug": "rome-thorstenson-2V9W",
                "email": "romethorstenson@gmail.com",
                "first_name": "Rome",
                "last_name": "Andreson",
                "university": "Harvard University",
                "major_or_grad_program": [
                    "Economics"
                ],
                "graduation_date": 2025,
                "company": "Goldman Sachs",
                "tag_line": "Goldman IB",
                "profile_picture": "https://images.prospinity.com/profile_pictures/public_images/user3.png",
                "profile_picture_url": "https://images.prospinity.com/profile_pictures/public_images/user3.png",
                "portfolio_count": 19,
                "pooled": true,
                "is_verified": true
            },
            {
                "slug": "hassaan-qadir-OmgW",
                "email": "hassaan.qadir@yale.edu",
                "first_name": "Emma",
                "last_name": "Green",
                "university": "Princeton University",
                "major_or_grad_program": [
                    "Ethics, Politics & Economics"
                ],
                "graduation_date": 2026,
                "company": "BCG",
                "tag_line": "Consultant @ BCG",
                "profile_picture": "https://images.prospinity.com/profile_pictures/public_images/user4.png",
                "profile_picture_url": "https://images.prospinity.com/profile_pictures/public_images/user4.png",
                "portfolio_count": 36,
                "pooled": true,
                "is_verified": true
            },
            {
                "slug": "joshua-zhu-OoG1",
                "email": "joshua.zhu@yale.edu",
                "first_name": "David",
                "last_name": "Glen",
                "university": "Columbia University",
                "major_or_grad_program": [
                    "Philosophy"
                ],
                "graduation_date": 2024,
                "company": "Founder",
                "tag_line": "Founder of an AI startup | Rased 300K+",
                "profile_picture": "https://images.prospinity.com/profile_pictures/public_images/user.png",
                "profile_picture_url": "https://images.prospinity.com/profile_pictures/public_images/user.png",
                "portfolio_count": 19,
                "pooled": true,
                "is_verified": true
            },
            {
                "slug": "brooks-leitner-ObLB",
                "email": "brooks.leitner@yale.edu",
                "first_name": "Arthur",
                "last_name": "Leitner",
                "university": "Yale University",
                "major_or_grad_program": [
                    "Law J.D."
                ],
                "graduation_date": 2024,
                "company": "Skadden",
                "tag_line": "Incoming First-Year Associate at Skadden",
                "profile_picture": "https://images.prospinity.com/profile_pictures/public_images/user6.png",
                "profile_picture_url": "https://images.prospinity.com/profile_pictures/public_images/user6.png",
                "portfolio_count": 19,
                "pooled": true,
                "is_verified": true
            },
        ],
        "captain_info": {
            "slug": "prospinity-captain-O4zW",
            "first_name": "Prospinity",
            "last_name": "Captain",
            "email": "main@prospinity.com"
        },
        "user_info": {
            "status": userStatus,
            "is_captain": false
        }
    }

    useEffect(() => {
        // Check if this is the special user (karen-newalk-bNXx)
        if (userSlug === "karen-newalk-bNXx" || userSlug === "sam-antonyan-lj42" ||  userSlug === "andrea-zanon-WYpp") {
            // Set initial status to accepted_not_signed
            setUserStatus("accepted_not_signed");
            
            // Set a timer to change status to accepted_signed after 20 seconds
            const timer = setTimeout(() => {
                setUserStatus("accepted_signed");
            }, 20000); // 20 seconds
            
            // Clean up the timer when component unmounts
            return () => clearTimeout(timer);
        }
    }, [userSlug]);

    return (
        <PageWrapper theme={theme}>
            <PoolHeaderWrapper>
                <PoolHeader pool={poolData} />
        
                {/* Tabs */}
                <Tabs>
                    {tabOptions.map((tab) => (
                        <Tab
                            key={tab}
                            isActive={activeTab === tab}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab.toUpperCase()}
                        </Tab>
                    ))}
                </Tabs>
            </PoolHeaderWrapper>
    
            {/* Tab Content (just placeholder for now) */}
            <MainContainer>
                    <>
                        {activeTab === "about" && 
                            <>
                                {userStatus === "member" && (
                                    <>
                                        <div style={{ width: "100%", display: "flex", flexDirection: isMobile ? "column" : "row", marginTop: isMobile ? "1rem" : "1.5rem", gap: isMobile ? "1rem" : "1.5rem" }}>
                                            <PoolMeeting />
                                            <PoolGroupMe />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <AboutPool pool={poolData} />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <PoolTimeGraphic pool={poolData} />
                                        </div>
                                    </>
                                )}

                                {userStatus === "accepted_not_signed" && (
                                    <>
                                        <Banner bannerType="matched" fullBanner={true} userName={userFirstName} pool={poolData} />

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <AboutPool pool={poolData} />
                                        </div>

                                        <div style={{ width: "100%", display: "flex", flexDirection: isMobile ? "column" : "row", marginTop: isMobile ? "1rem" : "1.5rem", gap: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool next meeting is locked" />
                                            <SectionLocked text="Pool group chat is locked" />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool group chat is locked" />
                                        </div>
                                    </>
                                )}

                                {userStatus === "accepted_signed" && (
                                    <>
                                        <Banner bannerType="matched" fullBanner={true} userName={userFirstName} pool={poolData} />

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <AboutPool pool={poolData} />
                                        </div>

                                        <div style={{ width: "100%", display: "flex", flexDirection: isMobile ? "column" : "row", marginTop: isMobile ? "1rem" : "1.5rem", gap: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool next meeting is locked" />
                                            <SectionLocked text="Pool group chat is locked" />
                                        </div>

                                        <div style={{ width: "100%", marginTop: isMobile ? "1rem" : "1.5rem" }}>
                                            <SectionLocked text="Pool group chat is locked" />
                                        </div>
                                    </>
                                )}
                            </>
                        }

                        {activeTab === "members" && 
                            <PortfolioGrid>
                                {poolData.other_users.map((user, index) => (
                                    <UserPoolCard key={index} user={user} clickable={false} />
                                ))}
                            </PortfolioGrid>
                        }

                        {activeTab === "faqs" && 
                            <PoolFAQ />
                        }

                        {activeTab === "vote" && 
                            <ContentWrapper>
                                <Content>
                                    <ClockIcon />
                                    <Text style={{ fontWeight: "500" }}>
                                        No requests yet
                                    </Text>
                                    <Subtext>People who request to join {poolData.public_details.name} will appear here</Subtext>
                                </Content>
                            </ContentWrapper>
                        }
                    </>
            </MainContainer>
        </PageWrapper>
    );
}


const PageWrapper = styled.div`
    background: ${({ theme }) => theme.backgroundDarker};
    min-height: 100vh;
`;

const PoolHeaderWrapper = styled.div`
    background: ${({ theme }) => theme.white};
    padding: 0 2.5rem;

    @media (max-width: 600px) {
        padding: 0 1rem;
    }
`;

const Tabs = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-top: 1rem;
    gap: 0.5rem;

    @media (max-width: 600px) {

    }
`;

const Tab = styled.div`
    font-size: 1.125rem;
    padding: 1rem 0.75rem;
    color: ${({ isActive, theme }) =>
        isActive ? theme.textPrimary : theme.textSecondary};
    font-weight: ${({ isActive }) => (isActive ? 500 : 400)};
    cursor: pointer;
    position: relative;

    &:after {
        content: "";
        display: ${({ isActive }) => (isActive ? "block" : "none")};
        height: 2px;
        background: ${({ theme }) => theme.primary};
        width: 100%;
        position: absolute;
        bottom: -1px;
        left: 0;
    }

    @media (max-width: 600px) {
        padding: 1rem 0.5rem;
        font-size: 1rem;
    }
`;


// Grid layout for portfolio cards
const PortfolioGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(260px, 1fr)); /* 3 cards per row initially */
    gap: 1rem;
    width: 100%;
    justify-content: center;

    @media (max-width: 1280px) {
        grid-template-columns: repeat(2, minmax(260px, 1fr)); /* 3 cards per row */
    }

    @media (max-width: 960px) {
        grid-template-columns: repeat(1, minmax(260px, 1fr)); /* 2 per row */
        place-items: center; 
    }

    @media (max-width: 600px) {
        grid-template-columns: repeat(1, 1fr); /* Full width */
        place-items: center; 
    }
`;

const ClockIcon = styled(NothingYetIcon)`
    circle {
        fill: ${({ theme }) => theme.white};
        r: 23;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
    margin-top: 4.5rem;

    @media (max-width: 600px) {
        margin-top: 0rem;
        margin-bottom: 0.5rem;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

const Text = styled.span`
    font-size: 1.25rem;
    color: ${({ theme }) => theme.textPrimary};
`;

const Subtext = styled.span`
    font-size: 1rem;
    color: ${({ theme }) => theme.textSecondary};
    margin-top: 0.25rem;
`;
